import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import isNil from "lodash/isNil";
import { getAttribute } from "PFCore/helpers/get_attribute";
import { useErrorsGrowl } from "PFCore/hooks/use_errors_growl";
import { FetchOptionsResponse, GroupOption, Id, Option, SelectOptions } from "PFTypes";
import { useEffect } from "react";

import { optionsKeys } from "./query_keys";
import { filterOptions } from "./use_options.utils";

export type UseOptions<T> = {
  dropdownId: string;
  searchId?: Id;
  ids?: Id[];
  searchTerm?: string;
  enabled?: boolean;
  options?: SelectOptions<T>;
  fetchOptions?: (payload: { ids?: Id[]; searchTerm?: string }) => Promise<FetchOptionsResponse<T>>;
  parseOptions?: (options: Option<T>[] | GroupOption<T>[]) => Option<T>[] | GroupOption<T>[];
  queryOptions?: Omit<UseQueryOptions<FetchOptionsResponse<T>>, "queryKey" | "queryFn">;
};

type UseOptionsReturn<T> = {
  data: SelectOptions<T>;
  total: number;
  isFetching: boolean;
  limit?: number;
};

export const useOptions = <T>({
  dropdownId,
  searchId,
  ids,
  searchTerm,
  options,
  fetchOptions,
  parseOptions,
  queryOptions = {}
}: UseOptions<T>): UseOptionsReturn<T> => {
  const isFetchingOptionsEnabled = !!fetchOptions;
  const growlErrors = useErrorsGrowl();
  const enabled = isFetchingOptionsEnabled && (!!queryOptions.enabled || isNil(queryOptions.enabled));
  const {
    data: fetchedOptions,
    isFetching,
    error
  } = useQuery({
    queryKey: optionsKeys.dropdownWithParams(dropdownId, searchId, ids, searchTerm),
    queryFn: () => (fetchOptions ? fetchOptions({ ids, searchTerm }) : undefined),
    ...queryOptions,
    enabled
  });

  useEffect(() => {
    if (error) {
      growlErrors(error);
    }
  }, [error, growlErrors]);

  if (isFetchingOptionsEnabled) {
    const data = fetchedOptions?.entries ?? [];
    return {
      data: parseOptions ? (parseOptions(data) as SelectOptions<T>) : data,
      total: fetchedOptions?.meta?.total || 0,
      limit: getAttribute(fetchedOptions?.meta, "selectedOptionsLimit"),
      isFetching
    };
  }

  const resultOptions = searchTerm ? filterOptions(options ?? [], searchTerm) : (options ?? []);
  return {
    data: parseOptions ? (parseOptions(resultOptions) as SelectOptions<T>) : resultOptions,
    total: resultOptions.length,
    isFetching: false
  };
};
